import { backendUrl } from "../backendUrl";
import React, { ChangeEvent, useEffect, useState } from "react";
import { Box, Slider } from "@mui/material";
import "./style.css";
import axios from "axios";
import { ConfigurationDetail } from "./types";


interface SortProps {
  configurationOriginal: ConfigurationDetail;
  // setter stanu z ChooseLockSystem - filtr aktualizuje wyniki funkcją (prev => ...)
  onConfigurationChange: React.Dispatch<React.SetStateAction<ConfigurationDetail>>;
  price: Price;
}

interface Price {
  minPrice: string;
  maxPrice: string;
}

interface Supplier {
  id: number;
  name: string;
  icon_path: string;
  display_order: number;
  is_active: number;
}

const Sort: React.FC<SortProps> = ({
  configurationOriginal,
  onConfigurationChange,
  price,
}) => {
  // Detekcja urządzeń mobilnych (spójnie z ChooseLockSystem)
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const detectMobile = () => {
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isMobileUserAgent = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isMobileScreen = window.innerWidth < 960; // zwiększony breakpoint
      
      return isTouchDevice && (isMobileUserAgent || isMobileScreen);
    };

    const handleResize = () => {
      setIsMobile(detectMobile());
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const [isInitialized, setIsInitialized] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>("");
  const [, setOpenManufacturerList] =
    useState<boolean>(false);

  const [value, setValue] = useState<number[]>([
    parseFloat(price.minPrice) || 0,
    parseFloat(price.maxPrice) || 0,
  ]);

  // Fetch suppliers from database
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/v1/configurator/suppliers/active`);
        if (response && response.data) {
          setSuppliers(response.data);
        }
      } catch (error) {
        console.error("Błąd podczas pobierania dostawców:", error);
      }
    };

    fetchSuppliers();
  }, []);

  useEffect(() => {
    if (!isInitialized && price.minPrice && price.maxPrice) {
      setValue([parseFloat(price.minPrice), parseFloat(price.maxPrice)]);
      setIsInitialized(true); // Oznacz wartości jako zainicjalizowane
    }
    const filterData = () => {
      // Jeśli zakres jest domyślny (pełny), zwróć całą listę
      const min = parseFloat(price.minPrice);
      const max = parseFloat(price.maxPrice);
      if (
        value[0] === min && value[1] === max && selectedManufacturer === ""
      ) {
        return configurationOriginal.filteredResult;
      }
      let data;
      if (selectedManufacturer !== "") {
        data = configurationOriginal.filteredResult.filter(
          (el) =>
            el.supllier === selectedManufacturer &&
            el.price >= value[0] &&
            el.price <= value[1]
        );
      } else {
        data = configurationOriginal.filteredResult.filter(
          (el) => el.price >= value[0] && el.price <= value[1]
        );
      }
      return data;
    };

    // Debounce: wywołaj onConfigurationChange dopiero po 100ms od ostatniej zmiany
    const handler = setTimeout(() => {
      const data = filterData();
      onConfigurationChange((current) => ({ ...current, filteredResult: data }));
    }, 100);
    return () => clearTimeout(handler);
    // configurationOriginal zamiast sort: filtr ma działać na liście z nowej odpowiedzi serwera.
    // Przy zależności od sort nakładał starą kolejność 100 ms po kliknięciu (lista była krok za strzałką).
  }, [value, selectedManufacturer, configurationOriginal, price.minPrice, price.maxPrice, isInitialized, onConfigurationChange]);

  const handleChange = (event: Event, newValue: number | number[]) => {
    setValue(newValue as number[]);
  };

  // Pola "von" / "bis": w trakcie wpisywania trzymamy tekst z pola (da się je wyczyścić
  // i wpisać nową kwotę), a lista filtruje się na bieżąco, gdy tekst jest liczbą
  const [draft, setDraft] = useState<[string | null, string | null]>([null, null]);
  // Zanim przyjdzie wycena, zakres cen nie jest znany - puste pola i wyłączony suwak
  // zamiast "0 € bis 0 €"
  const priceKnown = Boolean(price.minPrice && price.maxPrice);

  const handleChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    const index = event.target.name === "min" ? 0 : 1;
    const text = event.target.value;
    setDraft((current) => (index === 0 ? [text, current[1]] : [current[0], text]));
    const typed = parseFloat(text);
    if (!isNaN(typed)) {
      const updatedValue = [...value];
      updatedValue[index] = typed;
      setValue(updatedValue);
    }
  };

  // Po wyjściu z pola (lub Enter) kwota wraca do zakresu cen z wyceny, a "von" nie może być
  // większe niż "bis". Puste pole = brak ograniczenia z tej strony.
  const commitInput = (index: number) => {
    const min = Number(price.minPrice);
    const max = Number(price.maxPrice);
    const text = draft[index];
    setDraft([null, null]);
    if (!price.minPrice || !price.maxPrice || isNaN(min) || isNaN(max)) return;
    const updatedValue = [...value];
    if (text !== null && isNaN(parseFloat(text))) {
      updatedValue[index] = index === 0 ? min : max;
    }
    const clamp = (amount: number) => Math.min(Math.max(amount, min), max);
    let from = clamp(updatedValue[0]);
    let to = clamp(updatedValue[1]);
    if (from > to) [from, to] = [to, from];
    if (from !== value[0] || to !== value[1]) setValue([from, to]);
  };


  // Get unique suppliers from database, sorted by display_order
  const uniqueSuppliers = suppliers
    .sort((a, b) => a.display_order - b.display_order)
    .map((supplier) => supplier.name);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: "flex-start",
        alignItems: isMobile ? "stretch" : "center",
        gap: isMobile ? "20px" : "30px",
        marginBottom: "20px",
        width: "100%",
        padding: isMobile ? "16px" : "20px",
        backgroundColor: "#f8f9fa",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        border: "1px solid #e9ecef"
      }}
    >
      {/* Filtr producenta */}
      <div style={{ 
        display: "flex", 
        flexDirection: "column",
        minWidth: isMobile ? "100%" : "200px",
        flex: isMobile ? "1" : "0 0 auto"
      }}>
        <label style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#495057",
          marginBottom: "8px"
        }}>
          Hersteller
        </label>
        <select
          value={selectedManufacturer}
          onChange={(e) => {
            setSelectedManufacturer(e.target.value);
            setOpenManufacturerList(false);
          }}
          style={{
            width: "100%",
            padding: "12px 16px",
            border: "2px solid #e9ecef",
            outline: "none",
            borderRadius: "8px",
            backgroundColor: "white",
            fontSize: "14px",
            color: "#495057",
            transition: "all 0.2s ease",
            cursor: "pointer"
          }}
          onFocus={(e) => e.target.style.borderColor = "#005CA9"}
          onBlur={(e) => e.target.style.borderColor = "#e9ecef"}
        >
          <option value="">Alle Hersteller</option>
          {uniqueSuppliers.map((item) => {
            return (
              <option key={item} value={item}>
                {item}
              </option>
            );
          })}
        </select>
      </div>

      {/* Filtr cenowy */}
      <div style={{ 
        display: "flex", 
        flexDirection: "column",
        flex: "1",
        minWidth: isMobile ? "100%" : "300px"
      }}>
        <label style={{
          fontSize: "14px",
          fontWeight: "600",
          color: "#495057",
          marginBottom: "8px"
        }}>
          Preisbereich
        </label>
        
        {/* Pola input dla cen */}
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          marginBottom: "16px",
          flexWrap: isMobile ? "wrap" : "nowrap"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <span style={{ fontSize: "14px", color: "#6c757d", whiteSpace: "nowrap" }}>von</span>
            <input
              type="number"
              style={{
                width: "80px",
                padding: "8px 12px",
                border: "2px solid #e9ecef",
                borderRadius: "6px",
                textAlign: "center",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.2s ease"
              }}
              placeholder={price.minPrice}
              value={draft[0] ?? (priceKnown ? value[0] : "")}
              disabled={!priceKnown}
              name="min"
              onChange={handleChangeInput}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              onFocus={(e) => e.target.style.borderColor = "#005CA9"}
              onBlur={(e) => {
                e.target.style.borderColor = "#e9ecef";
                commitInput(0);
              }}
            />
            <span style={{ fontSize: "14px", color: "#6c757d" }}>€</span>
          </div>
          
          <span style={{ fontSize: "14px", color: "#6c757d" }}>bis</span>
          
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <input
              type="number"
              style={{
                width: "80px",
                padding: "8px 12px",
                border: "2px solid #e9ecef",
                borderRadius: "6px",
                textAlign: "center",
                fontSize: "14px",
                outline: "none",
                transition: "border-color 0.2s ease"
              }}
              placeholder={price.maxPrice}
              value={draft[1] ?? (priceKnown ? value[1] : "")}
              disabled={!priceKnown}
              name="max"
              onChange={handleChangeInput}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
              onFocus={(e) => e.target.style.borderColor = "#005CA9"}
              onBlur={(e) => {
                e.target.style.borderColor = "#e9ecef";
                commitInput(1);
              }}
            />
            <span style={{ fontSize: "14px", color: "#6c757d" }}>€</span>
          </div>
        </div>

        {/* Suwak cenowy */}
        <div style={{ 
          width: "100%",
          paddingTop: "8px"
        }}>
          <Box sx={{ 
            width: "100%",
            "& .MuiSlider-thumb": {
              backgroundColor: "#005CA9",
              border: "2px solid #ffffff",
              boxShadow: "0 2px 8px rgba(0,92,169,0.3)",
              "&:hover": {
                backgroundColor: "#004494",
                boxShadow: "0 4px 12px rgba(0,92,169,0.4)"
              }
            },
            "& .MuiSlider-track": {
              backgroundColor: "#005CA9",
              height: "6px",
              border: "none"
            },
            "& .MuiSlider-rail": {
              backgroundColor: "#e9ecef",
              height: "6px"
            },
            "& .MuiSlider-valueLabel": {
              backgroundColor: "#005CA9",
              color: "white",
              fontWeight: "600"
            }
          }}>
            <Slider
              getAriaLabel={() => "Preisbereich"}
              value={[Number(value[0]) || 0, Number(value[1]) || 0]}
              min={Number(price.minPrice) || 0}
              max={Number(price.maxPrice) || 1}
              // Krok 0,01 €: przy domyślnym kroku 1 liczonym od ceny minimalnej (np. 70,91)
              // uchwyt kończył się na 849,91 zamiast 850,08 i po przesunięciu drugiego
              // uchwytu filtr ukrywał najdroższy system
              step={0.01}
              disabled={!priceKnown}
              onChange={handleChange}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `${value}€`}
            />
          </Box>
        </div>
      </div>
    </div>
  );
};

export default Sort;
